import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const payload = new FormData(form);
      if (honeypotInput && honeypotInput.value.trim() === '') {
        payload.delete('phone_alt');
      }

      const response = await fetch('https://readdy.ai/api/form/d9p0bj0senbniolesn8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩漫大全',
    description: siteInfo.description,
    url: 'https://hanmandq.com/about',
  };

  const stats = [
    { label: '漫画总数', value: siteInfo.stats.totalComics, icon: 'ri-book-open-line' },
    { label: '日访问量', value: siteInfo.stats.dailyVisitors, icon: 'ri-user-heart-line' },
    { label: '月更新量', value: siteInfo.stats.monthlyUpdates, icon: 'ri-upload-cloud-line' },
    { label: '用户满意度', value: siteInfo.stats.userSatisfaction, icon: 'ri-emotion-happy-line' },
  ];

  return (
    <>
      <SEOHead
        title="关于我们_韩漫大全_韩国漫画在线阅读平台"
        description={siteInfo.description}
        keywords="韩漫大全,关于我们,韩国漫画,免费阅读,品牌介绍,联系我们"
        canonicalUrl="https://hanmandq.com/about"
        schemaData={aboutSchema}
      />
      <Navbar />
      <main className="pt-20 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: '关于我们' },
            ]}
          />

          {/* Brand Intro */}
          <section className="mb-16">
            <div className="text-center mb-10">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-4">
                关于 <span className="text-primary-500">韩漫大全</span>
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                {siteInfo.description}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-100 rounded-2xl p-6 text-center border border-background-200/60"
                >
                  <i className={`${stat.icon} text-2xl text-primary-500 w-8 h-8 flex items-center justify-center mx-auto mb-3`}></i>
                  <p className="text-2xl font-bold text-foreground-900 mb-1">{stat.value}</p>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Brand Story */}
          <section className="mb-16">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="lg:w-1/2">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20cozy%20digital%20reading%20space%20with%20a%20tablet%20displaying%20colorful%20Korean%20webtoon%20comics%2C%20soft%20warm%20lighting%20from%20a%20desk%20lamp%2C%20a%20cup%20of%20tea%20nearby%2C%20modern%20minimalist%20interior%2C%20cream%20and%20beige%20tones%2C%20peaceful%20reading%20atmosphere%2C%20editorial%20photography&width=700&height=500&seq=about-brand-story&orientation=landscape"
                  alt="韩漫大全 品牌故事"
                  title="韩漫大全 品牌故事"
                  className="w-full rounded-2xl"
                />
              </div>
              <div className="lg:w-1/2">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-5">
                  我们的故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    韩漫大全诞生于对韩国漫画的热爱。我们是一群资深的韩漫爱好者，深知华语读者在寻找高质量韩国漫画资源时面临的困难——语言障碍、更新缓慢、资源分散。
                  </p>
                  <p>
                    因此我们创立了韩漫大全，致力于打造一个专注于韩国漫画的综合在线阅读平台。从最初的几十部漫画起步，如今我们已收录超过 {siteInfo.stats.totalComics} 部韩国漫画作品，覆盖恋爱、奇幻、动作、悬疑、治愈等多个类型。
                  </p>
                  <p>
                    我们的团队每天密切关注韩国漫画市场动态，确保热门连载能够在第一时间翻译并呈现给华语读者。我们相信，每一部优秀的韩国漫画都值得被更多读者看到。
                  </p>
                  <p>
                    未来，韩漫大全将继续扩充漫画资源库，优化阅读体验，为更多热爱韩国漫画的读者提供优质的在线阅读服务。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-3">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">来自读者的真实评价</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '小陈',
                  role: '大学生',
                  content: '韩漫大全是我看过最好的韩漫网站！更新速度快，漫画种类全，最重要的是完全免费。以前找韩漫资源特别麻烦，现在只要有这一个网站就够了。',
                  rating: 5,
                },
                {
                  name: '老王',
                  role: '上班族',
                  content: '每天通勤路上必看韩漫大全，已经追了十几部漫画了。界面干净没广告，阅读体验很棒。推荐了好几个同事一起来看。',
                  rating: 5,
                },
                {
                  name: '小雨',
                  role: '漫画爱好者',
                  content: '从韩漫大全开办就一直在这里看漫画，看着它从一个只有几十部漫画的小网站成长到现在五千多部的规模，真的很感动。希望越做越好！',
                  rating: 5,
                },
              ].map((review) => (
                <div
                  key={review.name}
                  className="bg-background-100 rounded-2xl p-6 border border-background-200/60"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{review.content}&rdquo;</p>
                  <div>
                    <p className="font-semibold text-sm text-foreground-800">{review.name}</p>
                    <p className="text-xs text-foreground-500">{review.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                有任何问题或建议？欢迎随时联系我们，我们会尽快回复
              </p>

              <form
                onSubmit={handleSubmit}
                data-readdy-form=""
                className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/60"
              >
                <div className="space-y-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-300/60 text-sm text-foreground-700 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-300/60 text-sm text-foreground-700 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      value={formData.message}
                      onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-300/60 text-sm text-foreground-700 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    />
                    <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                  </div>

                  <div className="spam-field-wrap">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-center text-green-600 font-medium">
                      留言发送成功！我们会尽快回复您。
                    </p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-center text-red-500 font-medium">
                      {formError}
                    </p>
                  )}
                </div>
              </form>

              <div className="mt-8 text-center">
                <p className="text-sm text-foreground-500">
                  或直接发送邮件至：<strong className="text-foreground-700">contact@hanmandq.com</strong>
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}